import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Wifi, Tv, Satellite, ArrowRight, CheckCircle2 } from "lucide-react";
import { Header } from "../components/landing/Header";
import { Seo } from "../components/Seo";
import { Footer } from "../components/landing/Footer";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { TIMELINES, inputCls, Section, Field, CheckGroup, RadioPills } from "../components/intake/FormControls";
import { AddressAutocomplete } from "../components/intake/AddressAutocomplete";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CONNECTIVITY_OPTIONS = ["Business Internet & Cable TV", "Satellite Business Internet"];
const SITES = ["1 site", "2–5 sites", "6–20 sites", "20+ sites"];
const SPEEDS = ["Up to 100 Mbps", "100–500 Mbps", "1 Gbps", "Multi-Gig / Dedicated", "Not sure yet"];

const INITIAL = {
  services: [], org_name: "", full_name: "", email: "", phone: "", service_address: "",
  num_sites: "", desired_speed: "", tv_requirements: "", current_provider: "",
  decision_timeline: "", additional_notes: "", authorized: false, inquiry_type: "connectivity",
};

export default function ConnectivityInquiry() {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState(() => {
    const pre = searchParams.getAll("service").filter((s) => CONNECTIVITY_OPTIONS.includes(s));
    return { ...INITIAL, services: pre.length ? pre : [CONNECTIVITY_OPTIONS[0]] };
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const pick = (key) => (v) => setForm((f) => ({ ...f, [key]: v }));
  const toggleService = (opt) =>
    setForm((f) => ({ ...f, services: f.services.includes(opt) ? f.services.filter((s) => s !== opt) : [...f.services, opt] }));

  const wantsTv = form.services.includes("Business Internet & Cable TV");
  const satellite = form.services.includes("Satellite Business Internet");

  const submit = async (e) => {
    e.preventDefault();
    if (form.services.length === 0) { toast.error("Select at least one service."); return; }
    if (!form.org_name || !form.full_name || !form.email || !form.service_address) {
      toast.error("Please fill in Business Name, Contact Name, Email, and Service Address.");
      return;
    }
    if (!form.authorized) { toast.error("Please check the authorization box to submit."); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      setSubmitted(true);
      toast.success("Inquiry sent to our service desk.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed. Please try again or email service@silentsentrycorp.com.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="bg-[#05071F] text-white min-h-screen font-body">
      <Seo
        title="Business Internet, Cable TV & Satellite Inquiry | Silent Sentry, LLC"
        description="Request business internet, commercial cable TV, or satellite internet for your site. Quick form — our service desk replies within one business day with availability and pricing."
      />
      <Header />
      <main className="pt-16">
        <section className="border-b border-white/10 bg-[#0B1035]">
          <div className="max-w-4xl mx-auto px-6 py-16 md:py-20">
            <div className="flex items-center gap-3 mb-5">
              <Wifi className="w-7 h-7 text-cyan-400" /><Tv className="w-7 h-7 text-cyan-400" /><Satellite className="w-7 h-7 text-cyan-400" />
            </div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-violet-400 mb-4">Connectivity Services</p>
            <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-medium">Business Internet, Cable TV & Satellite Inquiry</h1>
            <p className="mt-6 text-slate-300 leading-relaxed max-w-2xl">
              A two-minute form. Tell us where you need service and what you need — our service desk checks availability across
              400+ carriers and replies within one business day with options and pricing.
            </p>
          </div>
        </section>

        <div className="max-w-4xl mx-auto px-6 py-16">
          {submitted ? (
            <div data-testid="connectivity-success" className="border border-cyan-400/30 bg-[#0B1035] p-10 text-center">
              <CheckCircle2 className="w-12 h-12 text-cyan-400 mx-auto" />
              <h2 className="mt-6 font-heading text-3xl font-medium">Inquiry received</h2>
              <p className="mt-4 text-slate-300 max-w-md mx-auto">
                Our service desk (service@silentsentrycorp.com) is checking availability at your address and will reply within one business day.
              </p>
              <Link to="/" data-testid="connectivity-back-home" className="mt-8 inline-flex items-center gap-2 bg-cyan-400 text-[#05071F] font-semibold px-7 py-3.5 rounded-full hover:bg-cyan-300 transition-colors">
                Back to Home <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ) : (
            <form data-testid="connectivity-form" onSubmit={submit} className="space-y-8">
              <Section num="1" title="What do you need?">
                <Field label="Service(s) *" full>
                  <CheckGroup prefix="conn-service" options={CONNECTIVITY_OPTIONS} values={form.services} onToggle={toggleService} />
                </Field>
                <Field label="Number of Sites" full>
                  <RadioPills prefix="conn-sites" options={SITES} value={form.num_sites} onChange={pick("num_sites")} />
                </Field>
                <Field label="Desired Download Speed" full>
                  <RadioPills prefix="conn-speed" options={SPEEDS} value={form.desired_speed} onChange={pick("desired_speed")} />
                </Field>
                {wantsTv && (
                  <Field label="TV Requirements (number of TVs, channels, sports packages…)" full>
                    <Input data-testid="input-conn-tv" className={inputCls} value={form.tv_requirements} onChange={set("tv_requirements")} placeholder="e.g. 12 TVs in lobby & guest rooms, DIRECTV sports package" />
                  </Field>
                )}
                <Field label={satellite && !wantsTv ? "Current Connectivity (if any)" : "Current Internet / TV Provider (if any)"} full>
                  <Input data-testid="input-conn-provider" className={inputCls} value={form.current_provider} onChange={set("current_provider")} placeholder="e.g. Cox Business 300 Mbps, contract ends Dec 2026" />
                </Field>
              </Section>

              <Section num="2" title="Where & who">
                <Field label="Service Address (street, city, state, ZIP) *" full>
                  <AddressAutocomplete testid="input-conn-address" value={form.service_address}
                    onChange={(v) => setForm((f) => ({ ...f, service_address: v }))} placeholder="Start typing — e.g. 1200 Farnam St, Omaha" />
                </Field>
                <Field label="Business Name *">
                  <Input data-testid="input-conn-org" className={inputCls} value={form.org_name} onChange={set("org_name")} placeholder="Riverside Dental Group" />
                </Field>
                <Field label="Contact Name *">
                  <Input data-testid="input-conn-name" className={inputCls} value={form.full_name} onChange={set("full_name")} placeholder="Jane Smith" />
                </Field>
                <Field label="Email Address *">
                  <Input data-testid="input-conn-email" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="jane@business.com" />
                </Field>
                <Field label="Phone">
                  <Input data-testid="input-conn-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="(402) 555-0123" />
                </Field>
              </Section>

              <Section num="3" title="Timing & notes">
                <Field label="When do you need service?" full>
                  <RadioPills prefix="conn-timeline" options={TIMELINES} value={form.decision_timeline} onChange={pick("decision_timeline")} />
                </Field>
                <Field label="Anything else we should know?" full>
                  <Textarea data-testid="input-conn-notes" className={inputCls} rows={3} value={form.additional_notes} onChange={set("additional_notes")} placeholder="Failover needs, static IPs, install constraints, budget…" />
                </Field>
                <Field label="" full>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <Checkbox data-testid="input-conn-authorized" checked={form.authorized}
                      onCheckedChange={(v) => setForm((f) => ({ ...f, authorized: Boolean(v) }))}
                      className="border-white/30 data-[state=checked]:bg-cyan-400 data-[state=checked]:text-[#05071F] mt-0.5" />
                    <span className="text-sm text-slate-300">I authorize Silent Sentry to check carrier availability at this address and contact me about connectivity options. *</span>
                  </label>
                </Field>
              </Section>

              <div className="flex flex-col sm:flex-row items-center gap-4">
                <button type="submit" data-testid="connectivity-submit-btn" disabled={submitting}
                  className="inline-flex items-center gap-2 whitespace-nowrap bg-cyan-400 text-[#05071F] font-semibold px-8 py-4 rounded-full hover:bg-cyan-300 transition-colors disabled:opacity-60">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />} Send to Service Desk
                </button>
                <p className="text-xs text-slate-500">Goes directly to service@silentsentrycorp.com. Need a full technology assessment instead? <Link to="/intake" className="text-cyan-400 hover:underline">Use the discovery form</Link>.</p>
              </div>
            </form>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
